import React, { useState, useEffect } from 'react';
import api, { licensePlateReportAPI } from '../services/api';
import Swal from 'sweetalert2';
import SearchableSelect from './SearchableSelect';
import SearchableMultiSelect from './SearchableMultiSelect';

const LicensePlateList = () => {
  const [plates, setPlates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [units, setUnits] = useState([]); // Thêm state cho danh sách đơn vị
  const [selectedPlates, setSelectedPlates] = useState([]); // State cho checkbox chọn nhiều
  const [forceReExport, setForceReExport] = useState(false); // State cho checkbox xuất lại
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    provinceId: '',
    unitIds: [], // Đổi từ unitId sang unitIds (array)
    color: '',
    unitType: '',
    vehicleType: '',
    startDate: '',
    endDate: ''
  });
  const [tempFilters, setTempFilters] = useState({
    provinceId: '',
    unitIds: [], // Đổi từ unitId sang unitIds (array)
    color: '',
    unitType: '',
    vehicleType: '',
    startDate: '',
    endDate: ''
  });

  // State cho form thêm biển số mới - nhiều dòng
  const [plateRows, setPlateRows] = useState([{
    id: Date.now(),
    provinceId: '',
    series: '',
    letter: '',
    number: '',
    color: 'trắng',
    unitType: 'bộ',
    vehicleType: 'ô tô'
  }]);

  useEffect(() => {
    fetchPlates();
    fetchProvinces();
    fetchUnits(); // Thêm fetch units
  }, [pagination.page, filters]);

  const fetchProvinces = async () => {
    try {
      const response = await api.get('/provinces');
      setProvinces(response.data);
      
      // Set Lâm Đồng mặc định cho dòng đầu tiên
      const lamDong = response.data.find(p => p.code === '49');
      if (lamDong && plateRows.length > 0) {
        setPlateRows(prev => prev.map((row, index) => 
          index === 0 ? { ...row, provinceId: lamDong.id, series: lamDong.code } : row
        ));
      }
    } catch (error) {
      console.error('Error fetching provinces:', error);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await api.get('/vehicle-registration/units');
      
      let unitsData = response.data;
      
      // Nếu có key 'units'
      if (unitsData.units && Array.isArray(unitsData.units)) {
        unitsData = unitsData.units;
      }
      // Nếu có key 'data'
      else if (unitsData.data && Array.isArray(unitsData.data)) {
        unitsData = unitsData.data;
      }
      // Nếu là object, convert values thành array
      else if (typeof unitsData === 'object' && !Array.isArray(unitsData)) {
        unitsData = Object.values(unitsData);
      }
      
      setUnits(Array.isArray(unitsData) ? unitsData : []);
    } catch (error) {
      console.error('Error fetching units:', error);
      setUnits([]);
    }
  };

  const fetchPlates = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Convert unitIds array thành comma-separated string
      if (filters.unitIds && filters.unitIds.length > 0) {
        params.unitIds = filters.unitIds.join(',');
      } else {
        delete params.unitIds; // Xóa nếu rỗng
      }

      const response = await api.get('/license-plates', { params });
      setPlates(response.data.data);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
      setSelectedPlates([]); // Reset checkbox khi load trang mới
    } catch (error) {
      console.error('Error fetching plates:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Lỗi khi tải danh sách biển số',
        confirmButtonColor: '#1976d2'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Xác nhận xóa',
      text: 'Bạn có chắc muốn xóa biển số này?',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Xóa',
      cancelButtonText: 'Hủy'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await api.delete(`/license-plates/${id}`);
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: 'Đã xóa biển số thành công',
        timer: 2000,
        showConfirmButton: false
      });
      fetchPlates();
    } catch (error) {
      console.error('Error deleting plate:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi xóa biển số',
        confirmButtonColor: '#1976d2'
      });
    }
  };

  // Xử lý chọn/bỏ chọn 1 biển số
  const handleToggleSelect = (plateId) => {
    setSelectedPlates(prev => {
      if (prev.includes(plateId)) {
        return prev.filter(id => id !== plateId);
      } else {
        return [...prev, plateId];
      }
    });
  };

  // Xử lý chọn/bỏ chọn tất cả biển số trên trang hiện tại
  const handleToggleSelectAll = () => {
    if (selectedPlates.length === plates.length) {
      setSelectedPlates([]);
    } else {
      setSelectedPlates(plates.map(plate => plate.id));
    }
  };

  // Xóa nhiều biển số đã chọn
  const handleDeleteSelected = async () => {
    if (selectedPlates.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Chưa chọn biển số',
        text: 'Vui lòng chọn ít nhất 1 biển số để xóa',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Xác nhận xóa',
      text: `Bạn có chắc muốn xóa ${selectedPlates.length} biển số đã chọn?`,
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Xóa',
      cancelButtonText: 'Hủy'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      
      // Xóa từng biển số
      const deletePromises = selectedPlates.map(id => 
        api.delete(`/license-plates/${id}`)
      );
      
      await Promise.all(deletePromises);
      
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: `Đã xóa ${selectedPlates.length} biển số`,
        timer: 2000,
        showConfirmButton: false
      });
      
      setSelectedPlates([]);
      fetchPlates();
    } catch (error) {
      console.error('Error deleting plates:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Lỗi khi xóa biển số',
        confirmButtonColor: '#1976d2'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setTempFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    setFilters(prev => ({ ...prev, ...tempFilters }));
    setPagination(prev => ({ ...prev, page: 1 }));
    setShowFilterModal(false);
  };

  const handleExportWord = async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.unitIds && filters.unitIds.length > 0) {
        params.unitIds = filters.unitIds.join(',');
      }
      if (filters.vehicleType) {
        params.vehicleType = filters.vehicleType; // ✅ Thêm filter loại xe
      }
      if (forceReExport) {
        params.forceReExport = 'true'; // Thêm flag nếu checkbox được chọn
      }
      
      const response = await licensePlateReportAPI.exportWordReport(params);
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Bao-cao-bien-so-${Date.now()}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      Swal.fire({
        icon: 'success',
        title: 'Thành công',
        text: forceReExport ? 'Xuất lại báo cáo Word thành công!' : 'Xuất báo cáo Word thành công!'
      });
      
      // Reset checkbox sau khi xuất thành công
      setForceReExport(false);
    } catch (error) {
      console.error('Error exporting Word report:', error);
      console.log('Error response:', error.response); // Debug
      console.log('Error message:', error.response?.data?.message); // Debug
      
      // Nếu lỗi 400 → Có thể là không có biển mới, hỏi user có muốn xuất lại không
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || '';
        
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Không có biển số mới',
          html: `
            <p>${errorMessage}</p>
            <p style="margin-top: 10px;"><strong>Bạn có muốn xuất lại tất cả biển số trong khoảng thời gian này không?</strong></p>
          `,
          showCancelButton: true,
          confirmButtonColor: '#1976d2',
          cancelButtonColor: '#6b7280',
          confirmButtonText: '✅ Xuất lại tất cả',
          cancelButtonText: 'Hủy'
        });
        
        if (result.isConfirmed) {
          // Xuất lại với forceReExport=true
          try {
            setLoading(true);
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;
            if (filters.unitIds && filters.unitIds.length > 0) {
              params.unitIds = filters.unitIds.join(',');
            }
            if (filters.vehicleType) {
              params.vehicleType = filters.vehicleType; // ✅ Thêm filter loại xe
            }
            params.forceReExport = 'true'; // ← Thêm flag xuất lại
            
            const response = await licensePlateReportAPI.exportWordReport(params);
            
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Bao-cao-bien-so-${Date.now()}.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            Swal.fire({
              icon: 'success',
              title: 'Thành công',
              text: 'Xuất lại báo cáo Word thành công!'
            });
          } catch (retryError) {
            console.error('Error re-exporting:', retryError);
            Swal.fire({
              icon: 'error',
              title: 'Lỗi',
              text: 'Lỗi khi xuất lại báo cáo: ' + (retryError.response?.data?.message || retryError.message)
            });
          } finally {
            setLoading(false);
          }
        }
      } else {
        // Lỗi khác (không phải 400)
        Swal.fire({
          icon: 'error',
          title: 'Lỗi',
          text: 'Lỗi khi xuất báo cáo Word: ' + (error.response?.data?.message || error.message)
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilter = () => {
    const resetFilters = {
      provinceId: '',
      unitIds: [], // Đổi sang array rỗng
      color: '',
      unitType: '',
      vehicleType: '',
      startDate: '',
      endDate: ''
    };
    setTempFilters(resetFilters);
    setFilters(prev => ({ ...prev, ...resetFilters }));
  };

  const handleSearchChange = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleOpenFilterModal = () => {
    // Sync tempFilters với filters hiện tại khi mở modal
    setTempFilters({
      provinceId: filters.provinceId,
      unitIds: filters.unitIds, // Sync unitIds
      color: filters.color,
      unitType: filters.unitType,
      vehicleType: filters.vehicleType,
      startDate: filters.startDate,
      endDate: filters.endDate
    });
    setShowFilterModal(true);
  };

  const handleOpenAddModal = () => {
    // Reset form về 1 dòng với Lâm Đồng mặc định
    const lamDong = provinces.find(p => p.code === '49');
    setPlateRows([{
      id: Date.now(),
      provinceId: lamDong?.id || '',
      series: lamDong?.code || '',
      letter: '',
      number: '',
      color: 'trắng',
      unitType: 'bộ',
      vehicleType: 'ô tô'
    }]);
    setShowAddModal(true);
  };

  const handleProvinceChange = (rowId, provinceId) => {
    const province = provinces.find(p => p.id === parseInt(provinceId));
    setPlateRows(prev => prev.map(row => 
      row.id === rowId 
        ? { ...row, provinceId, series: province ? province.code : '' }
        : row
    ));
  };

  const handleLetterChange = (rowId, value) => {
    const upperValue = value.toUpperCase();
    // Cho phép chữ cái (A-Z) và số (0-9), tối đa 2 ký tự
    if (upperValue.length <= 2 && /^[A-Z0-9]*$/.test(upperValue)) {
      setPlateRows(prev => prev.map(row => 
        row.id === rowId ? { ...row, letter: upperValue } : row
      ));
    }
  };

  const handleNumberChange = (rowId, value) => {
    if (value.length <= 5 && /^\d*$/.test(value)) {
      setPlateRows(prev => prev.map(row => 
        row.id === rowId ? { ...row, number: value } : row
      ));
    }
  };

  const handleFieldChange = (rowId, field, value) => {
    setPlateRows(prev => prev.map(row => {
      if (row.id === rowId) {
        const updatedRow = { ...row, [field]: value };
        
        // Nếu thay đổi vehicleType sang "mô tô", tự động set unitType = "biển"
        if (field === 'vehicleType' && value === 'mô tô') {
          updatedRow.unitType = 'biển';
        }
        // Nếu thay đổi vehicleType sang "ô tô", tự động set unitType = "bộ"
        else if (field === 'vehicleType' && value === 'ô tô') {
          updatedRow.unitType = 'bộ';
        }
        
        return updatedRow;
      }
      return row;
    }));
  };

  const isLastRowComplete = (row) => {
    return row.provinceId && row.letter && row.number.length === 5;
  };

  const areAllRowsComplete = () => {
    return plateRows.every(row => isLastRowComplete(row));
  };

  const handleAddRow = () => {
    const lastRow = plateRows[plateRows.length - 1];
    setPlateRows(prev => [...prev, {
      id: Date.now(),
      provinceId: lastRow.provinceId,
      series: lastRow.series,
      letter: '',
      number: '',
      color: lastRow.color,
      unitType: lastRow.unitType,
      vehicleType: lastRow.vehicleType
    }]);
  };

  const handleRemoveRow = (rowId) => {
    if (plateRows.length > 1) {
      setPlateRows(prev => prev.filter(row => row.id !== rowId));
    }
  };

  const handleAddPlates = async () => {
    // Validate tất cả các dòng
    const invalidRows = plateRows.filter(row => 
      !row.provinceId || !row.letter || row.number.length !== 5
    );

    if (invalidRows.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Thiếu thông tin',
        text: 'Vui lòng nhập đầy đủ thông tin cho tất cả các biển số',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    try {
      setLoading(true);
      
      const results = {
        success: [],
        failed: []
      };
      
      // Tạo từng biển số
      for (const row of plateRows) {
        const formattedNumber = `${row.number.slice(0, 3)}.${row.number.slice(3)}`;
        const fullPlate = `${row.series}${row.letter}-${formattedNumber}`;
        
        const payload = {
          provinceId: row.provinceId,
          series: `${row.series}${row.letter}`,
          number: formattedNumber,
          color: row.color,
          unitType: row.unitType,
          vehicleType: row.vehicleType
        };

        try {
          await api.post('/license-plates/single', payload);
          results.success.push(fullPlate);
        } catch (error) {
          results.failed.push({
            plate: fullPlate,
            reason: error.response?.data?.message || 'Lỗi không xác định'
          });
        }
      }

      // Hiển thị kết quả
      if (results.failed.length === 0 && results.success.length > 0) {
        // Tất cả thành công
        Swal.fire({
          icon: 'success',
          title: 'Thành công!',
          text: `Đã thêm ${results.success.length} biển số mới`,
          timer: 2000,
          showConfirmButton: false
        });
        
        setShowAddModal(false);
        fetchPlates(); // Refresh danh sách
      } else if (results.success.length === 0) {
        // Tất cả thất bại
        const failedList = results.failed.map(f => `${f.plate}: ${f.reason}`).join('<br>');
        Swal.fire({
          icon: 'error',
          title: 'Không thể thêm biển số',
          html: `<div style="text-align: left; max-height: 300px; overflow-y: auto;">${failedList}</div>`,
          confirmButtonColor: '#1976d2'
        });
      } else {
        // Một số thành công, một số thất bại
        const failedList = results.failed.map(f => `${f.plate}: ${f.reason}`).join('<br>');
        await Swal.fire({
          icon: 'warning',
          title: 'Hoàn thành một phần',
          html: `
            <div style="text-align: left;">
              <p><strong>✅ Thành công: ${results.success.length} biển số</strong></p>
              <p><strong>❌ Thất bại: ${results.failed.length} biển số</strong></p>
              <div style="max-height: 200px; overflow-y: auto; margin-top: 10px; padding: 10px; background: #f5f5f5; border-radius: 4px;">
                ${failedList}
              </div>
            </div>
          `,
          confirmButtonColor: '#1976d2'
        });
        
        // Xóa các dòng đã tạo thành công, giữ lại các dòng thất bại
        const successPlates = results.success.map(p => p.replace(/\./g, ''));
        setPlateRows(prev => prev.filter(row => {
          const rowPlate = `${row.series}${row.letter}-${row.number.slice(0, 3)}${row.number.slice(3)}`;
          return !successPlates.includes(rowPlate);
        }));
        
        fetchPlates(); // Refresh danh sách
      }
    } catch (error) {
      console.error('Error adding plates:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Lỗi không xác định khi thêm biển số',
        confirmButtonColor: '#1976d2'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="license-plate-list">
      <div className="list-header">
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1 }}>
          <input
            type="text"
            placeholder="Tìm kiếm biển số..."
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="search-input-main"
          />
          <button 
            className="btn-add-new"
            onClick={handleOpenAddModal}
          >
            ➕ Thêm biển số
          </button>
          <button 
            className="btn-filter"
            onClick={handleOpenFilterModal}
          >
            🔍 Lọc
          </button>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '5px', 
            cursor: 'pointer', 
            fontSize: '14px',
            padding: '8px 12px',
            borderRadius: '4px',
            backgroundColor: forceReExport ? '#fff3cd' : '#f8f9fa',
            border: forceReExport ? '2px solid #ffc107' : '2px solid #dee2e6',
            transition: 'all 0.2s',
            fontWeight: forceReExport ? '600' : 'normal',
            color: forceReExport ? '#856404' : '#495057'
          }}>
            <input
              type="checkbox"
              checked={forceReExport}
              onChange={(e) => setForceReExport(e.target.checked)}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
            <span>🔄 Xuất lại tất cả</span>
          </label>
          <button 
            className="btn-export"
            onClick={handleExportWord}
            disabled={loading}
          >
            📄 Xuất Word
          </button>
        </div>
        {selectedPlates.length > 0 && (
          <button 
            className="btn-delete-selected"
            onClick={handleDeleteSelected}
          >
            🗑️ Xóa ({selectedPlates.length})
          </button>
        )}
      </div>

      {/* Add Plate Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Thêm biển số mới</h3>
              <button 
                className="btn-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="plate-rows-container">
                <div className="plate-row-header">
                  <div className="header-cell">Tỉnh/TP</div>
                  <div className="header-cell">Loại xe</div>
                  <div className="header-cell">Seri</div>
                  <div className="header-cell">Số biển</div>
                  <div className="header-cell">Màu sắc</div>
                  <div className="header-cell">Đơn vị</div>
                  <div className="header-cell">Thao tác</div>
                </div>

                {plateRows.map((row, index) => (
                  <div key={row.id} className="plate-row">
                    <div className="row-cell province-cell">
                      <SearchableSelect
                        options={provinces.map(p => ({
                          value: p.id,
                          label: `${p.code} - ${p.name}`
                        }))}
                        value={row.provinceId}
                        onChange={(value) => handleProvinceChange(row.id, value)}
                        placeholder="Chọn tỉnh/TP"
                      />
                    </div>

                    <div className="row-cell">
                      <select
                        value={row.vehicleType}
                        onChange={(e) => handleFieldChange(row.id, 'vehicleType', e.target.value)}
                        required
                      >
                        <option value="ô tô">Ô tô</option>
                        <option value="mô tô">Mô tô</option>
                        <option value="rơ moóc">Rơ moóc</option>
                      </select>
                    </div>

                    <div className="row-cell series-cell">
                      <input
                        type="text"
                        value={row.series}
                        disabled
                        className="series-code-inline"
                      />
                      <input
                        type="text"
                        value={row.letter}
                        onChange={(e) => handleLetterChange(row.id, e.target.value)}
                        placeholder="AB/A1"
                        maxLength="2"
                        className="series-letter-inline"
                        required
                      />
                    </div>

                    <div className="row-cell">
                      <input
                        type="text"
                        value={row.number}
                        onChange={(e) => handleNumberChange(row.id, e.target.value)}
                        placeholder="00001"
                        maxLength="5"
                        required
                      />
                    </div>

                    <div className="row-cell">
                      <select
                        value={row.color}
                        onChange={(e) => handleFieldChange(row.id, 'color', e.target.value)}
                        required
                      >
                        <option value="trắng">Trắng</option>
                        <option value="vàng">Vàng</option>
                        <option value="xanh">Xanh</option>
                      </select>
                    </div>

                    <div className="row-cell">
                      <select
                        value={row.unitType}
                        onChange={(e) => handleFieldChange(row.id, 'unitType', e.target.value)}
                        required
                      >
                        {row.vehicleType === 'mô tô' ? (
                          // Mô tô: chỉ có "Biển"
                          <option value="biển">Biển</option>
                        ) : (
                          // Ô tô/Rơ moóc: có các options khác (không có "biển")
                          <>
                            <option value="bộ">Bộ</option>
                            <option value="bộ dài">Bộ dài</option>
                            <option value="bộ ngắn">Bộ ngắn</option>
                            <option value="biển dài">Biển dài</option>
                            <option value="biển ngắn">Biển ngắn</option>
                          </>
                        )}
                      </select>
                    </div>

                    <div className="row-cell actions-cell">
                      {index === plateRows.length - 1 ? (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            type="button"
                            onClick={handleAddRow}
                            disabled={!isLastRowComplete(row)}
                            className="btn-add-row"
                            title={isLastRowComplete(row) ? "Thêm dòng" : "Vui lòng nhập đủ thông tin"}
                          >
                            +
                          </button>
                          {plateRows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(row.id)}
                              className="btn-remove-row"
                              title="Xóa dòng này"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRemoveRow(row.id)}
                          className="btn-remove-row"
                          title="Xóa dòng"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-reset"
                onClick={() => setShowAddModal(false)}
              >
                Hủy
              </button>
              <button 
                className="btn-apply"
                onClick={handleAddPlates}
                disabled={loading || !areAllRowsComplete()}
              >
                {loading ? 'Đang thêm...' : `Thêm ${plateRows.length} biển số`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bộ lọc</h3>
              <button 
                className="btn-close"
                onClick={() => setShowFilterModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="filter-row">
                <div className="filter-group">
                  <label>Từ ngày</label>
                  <input
                    type="date"
                    value={tempFilters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label>Đến ngày</label>
                  <input
                    type="date"
                    value={tempFilters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="filter-input"
                  />
                </div>
              </div>

              <div className="filter-row">
                <div className="filter-group">
                  <label>Màu sắc</label>
                  <select
                    value={tempFilters.color}
                    onChange={(e) => handleFilterChange('color', e.target.value)}
                    className="filter-input"
                  >
                    <option value="">Tất cả màu</option>
                    <option value="trắng">Trắng</option>
                    <option value="vàng">Vàng</option>
                    <option value="xanh">Xanh</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Loại xe</label>
                  <select
                    value={tempFilters.vehicleType}
                    onChange={(e) => handleFilterChange('vehicleType', e.target.value)}
                    className="filter-input"
                  >
                    <option value="">Tất cả loại xe</option>
                    <option value="ô tô">Ô tô</option>
                    <option value="mô tô">Mô tô</option>
                    <option value="rơ moóc">Rơ moóc</option>
                  </select>
                </div>
              </div>

              <div className="filter-row">
                <div className="filter-group">
                  <label>Đơn vị biển</label>
                  <select
                    value={tempFilters.unitType}
                    onChange={(e) => handleFilterChange('unitType', e.target.value)}
                    className="filter-input"
                  >
                    <option value="">Tất cả đơn vị</option>
                    <option value="bộ">Bộ</option>
                    <option value="bộ dài">Bộ dài</option>
                    <option value="bộ ngắn">Bộ ngắn</option>
                    <option value="biển dài">Biển dài</option>
                    <option value="biển ngắn">Biển ngắn</option>
                  </select>
                </div>

                <div className="filter-group">
                  {/* Empty space để giữ layout 2 cột */}
                </div>
              </div>

              <div className="filter-row">
                <div className="filter-group full-width">
                  <label>Phường/Xã (có thể chọn nhiều)</label>
                  <SearchableMultiSelect
                    options={Array.isArray(units) ? units : []}
                    selectedValues={tempFilters.unitIds}
                    onChange={(values) => handleFilterChange('unitIds', values)}
                    placeholder="Chọn phường/xã..."
                    searchPlaceholder="Gõ để tìm kiếm..."
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-reset"
                onClick={handleResetFilter}
              >
                Đặt lại
              </button>
              <button 
                className="btn-apply"
                onClick={handleApplyFilter}
              >
                Áp dụng
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Đang tải...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="plates-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={plates.length > 0 && selectedPlates.length === plates.length}
                      onChange={handleToggleSelectAll}
                      title="Chọn tất cả"
                    />
                  </th>
                  <th>Biển số</th>
                  <th>Tỉnh/Thành phố</th>
                  <th>Loại xe</th>
                  <th>Màu sắc</th>
                  <th>Đơn vị</th>
                  <th>Phường/Xã</th>
                  <th>Người tạo</th>
                  <th>Ngày tạo</th>
                  <th>Trạng thái xuất</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {plates.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="no-data">Không có dữ liệu</td>
                  </tr>
                ) : (
                  plates.map(plate => (
                    <tr key={plate.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedPlates.includes(plate.id)}
                          onChange={() => handleToggleSelect(plate.id)}
                        />
                      </td>
                      <td className="plate-number">{plate.fullPlate}</td>
                      <td>{plate.province?.name}</td>
                      <td>{plate.vehicleType}</td>
                      <td>
                        <span className={`color-badge ${plate.color}`}>
                          {plate.color}
                        </span>
                      </td>
                      <td>{plate.unitType}</td>
                      <td>{plate.unit?.name}</td>
                      <td>{plate.creator?.username}</td>
                      <td>{new Date(plate.createdAt).toLocaleDateString('vi-VN')}</td>
                      <td>
                        {plate.exportedInDocumentNumber ? (
                          <span className="export-status exported" title={`Xuất lúc: ${new Date(plate.exportedAt).toLocaleString('vi-VN')}`}>
                            ✓ {plate.exportedInDocumentNumber}
                          </span>
                        ) : (
                          <span className="export-status not-exported">Chưa xuất</span>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => handleDelete(plate.id)}
                          className="btn-delete"
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn-page"
              >
                Trước
              </button>
              <span className="page-info">
                Trang {pagination.page} / {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="btn-page"
              >
                Sau
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LicensePlateList;
